import { useEffect, useState } from "react";
import { SOLAR } from "@/constants/testIds";
import { buildShareUrl } from "@/lib/shareLink";
import type { Place } from "@/hooks/usePlace";

const LINK = "font-sans text-xs text-[#8a6f52] underline decoration-[#d9c9ad] underline-offset-4 transition-colors hover:text-[#c0501b] hover:decoration-[#c0501b]";

async function share(url: string, label: string): Promise<string> {
  if (navigator.share) {
    try {
      await navigator.share({ title: "True Solar Time", text: `True solar time at ${label}`, url });
      return "";
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return "";
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return "Link copied";
  } catch {
    window.prompt("Copy this link:", url);
    return "";
  }
}

function ShareButton({ place }: { place: Place }) {
  const [status, setStatus] = useState("");
  useEffect(() => {
    if (!status) return;
    const id = window.setTimeout(() => setStatus(""), 2500);
    return () => window.clearTimeout(id);
  }, [status]);
  return (
    <>
      <button data-testid={SOLAR.shareBtn} onClick={async () => setStatus(await share(buildShareUrl(place), place.label))} className={LINK}>
        Share this place
      </button>
      {status && (
        <span data-testid={SOLAR.shareStatus} role="status" className="font-sans text-xs text-[#c0501b]">
          {status}
        </span>
      )}
    </>
  );
}

interface Props {
  place: Place;
  onForget: () => void;
  onLeaveShared: () => void;
}

export default function PlaceActions({ place, onForget, onLeaveShared }: Props) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
      <ShareButton place={place} />
      {place.source === "saved" && (
        <button data-testid={SOLAR.forgetBtn} onClick={onForget} className={LINK}>
          Forget this place
        </button>
      )}
      {place.source === "shared" && (
        <>
          <span data-testid={SOLAR.sharedNote} className="font-sans text-xs italic text-[#8a6f52]">
            Shared with you
          </span>
          <button data-testid={SOLAR.leaveSharedBtn} onClick={onLeaveShared} className={LINK}>
            Back to my place
          </button>
        </>
      )}
    </div>
  );
}
